import { CheckCircleOutlined } from "@ant-design/icons";
import { Alert, Empty, Input, Spin, Typography } from "antd";
import classNames from "classnames";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useState } from "react";

import { FormFieldSourceOption, getFormFieldSourceKey } from "./types";

interface FormFieldParentFieldSelectorProps {
    globalFields: FormFieldSourceOption[];
    localFields: FormFieldSourceOption[];
    selectedKey: string | null;
    onSelect: (key: string | null) => void;
    loading?: boolean;
    error?: boolean;
}

export const FormFieldParentFieldSelector = ({
    globalFields,
    localFields,
    selectedKey,
    onSelect,
    loading = false,
    error = false,
}: FormFieldParentFieldSelectorProps) => {
    const { t } = usePaldockTranslation("drawers.form-field-parent-field");
    const [search, setSearch] = useState("");
    const allFields = [...globalFields, ...localFields];

    const findExactMatch = (value: string) => {
        const normalizedValue = value.trim().toLocaleLowerCase();
        if (!normalizedValue) return null;

        const matches = allFields.filter(
            (field) =>
                !field.disabled &&
                [field.systemName, field.visibleDescription].some(
                    (candidate) => candidate.toLocaleLowerCase() === normalizedValue
                )
        );

        return matches.length === 1 ? matches[0] : null;
    };

    const exactMatch = findExactMatch(search);

    const filterFields = (fields: FormFieldSourceOption[]) => {
        const normalizedSearch = search.trim().toLocaleLowerCase();
        if (!normalizedSearch) return fields;

        return fields.filter((field) =>
            [field.systemName, field.visibleDescription].some((value) =>
                value.toLocaleLowerCase().includes(normalizedSearch)
            )
        );
    };

    const filteredGlobalFields = filterFields(globalFields);
    const filteredLocalFields = filterFields(localFields);

    const renderSection = (title: string, fields: FormFieldSourceOption[]) => (
        <section className="rounded-lg bg-white p-4">
            <Typography.Title level={5}>{title}</Typography.Title>
            {fields.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("empty")} />
            ) : (
                <div className="flex flex-wrap gap-2">
                    {fields.map((field) => {
                        const key = getFormFieldSourceKey(field.sourceType, field.id);
                        const selected = selectedKey === key;
                        const disabled = Boolean(field.disabled && !selected);

                        return (
                            <button
                                key={key}
                                type="button"
                                disabled={disabled}
                                className={classNames(
                                    "select-none rounded border-0 px-3 py-1 text-sm transition-colors",
                                    selected
                                        ? "bg-primary text-white"
                                        : "bg-transparent text-[#000000E0] hover:bg-[#0000000F]",
                                    disabled && "cursor-not-allowed opacity-40"
                                )}
                                onClick={() => onSelect(key)}
                            >
                                {field.visibleDescription || field.systemName}
                            </button>
                        );
                    })}
                </div>
            )}
        </section>
    );

    return (
        <Spin spinning={loading}>
            <div className="flex flex-col gap-4 rounded-lg bg-gray-100 p-3">
                <section className="rounded-lg bg-white p-4">
                    <Typography.Title level={5}>{t("select_title")}</Typography.Title>
                    <Input
                        allowClear
                        value={search}
                        prefix={exactMatch ? <CheckCircleOutlined data-testid="matched-field-icon" /> : undefined}
                        onChange={(event) => {
                            const value = event.target.value;
                            const match = findExactMatch(value);

                            setSearch(value);
                            if (match) {
                                onSelect(getFormFieldSourceKey(match.sourceType, match.id));
                            }
                        }}
                        placeholder={t("search_placeholder")}
                    />
                    <div className="mt-3">
                        <button
                            type="button"
                            className={classNames(
                                "select-none rounded border-0 px-3 py-1 text-sm transition-colors",
                                selectedKey === null
                                    ? "bg-primary text-white"
                                    : "bg-transparent text-[#000000E0] hover:bg-[#0000000F]"
                            )}
                            onClick={() => onSelect(null)}
                        >
                            {t("custom_field")}
                        </button>
                        <Typography.Paragraph type="secondary" className="!mb-0 !mt-2 text-sm">
                            {t("custom_field_description")}
                        </Typography.Paragraph>
                    </div>
                </section>

                {error && <Alert showIcon type="error" message={t("messages.load_error")} />}
                {renderSection(t("sections.global"), filteredGlobalFields)}
                {renderSection(t("sections.local"), filteredLocalFields)}
            </div>
        </Spin>
    );
};
